import BoundingBox from "App/utils/bounding_box";
import {FrustumVolume} from "App/utils/view_frustum";
import {vec3} from "gl-matrix";

const ctx: Worker = self as any;

export
class TransformData {
    private static ranks: Uint8Array = new Uint8Array([-1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, 62, -1, -1, -1, 63, 52, 53, 54, 55, 56, 57, 58, 59, 60, 61, -1, -1, -1, 0, -1, -1, -1, 0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, -1, -1, -1, -1, -1, -1, 26, 27, 28, 29, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 40, 41, 42, 43, 44, 45, 46, 47, 48, 49, 50, 51]);
    
    private static _convertBase64ToUint8array(aBase64String: string)
    {
        let len = aBase64String.length >>> 0;
        let arraybuffer = new ArrayBuffer((len >>> 1) + (len >>> 2));
        let buffer = new Uint8Array(arraybuffer);

        // if len == 0 then prevent the infinite do while
        if (len <= 0) {
            return buffer;
        }

        let i = -1 | 0;
        let outptr = -1 | 0;
        let last0 = 0;
        let last1 = 0;
        let state = 0;
        let save = 0;
        let rank;
        let code;

        do {
            code = aBase64String.charCodeAt(++i);
            rank = this.ranks[code];
            if (rank < 255) {
                last1 = last0;
                last0 = code;
                save = (save << 6) | rank;
                if (++state === 4) {
                    buffer[++outptr] = save >>> 16;
                    if (last1 !== 61) {
                        buffer[++outptr] = save >>> 8;
                    }
                    if (last0 !== 61) {
                        buffer[++outptr] = save;
                    }
                    state = 0;
                }
            }
        } while (--len);

        return arraybuffer;
    }

    public static base64ToUint8Array(aData: string): ArrayBuffer {
        return TransformData._convertBase64ToUint8array(aData);
    }
}

export
class DownloadQuery {
    private downloadWorker: DownloadWorker;
    private resource: any;
    private highPrSlotUsed: boolean;
    private decodedBase64Data: ArrayBuffer;

    constructor(aDownloadWorker: DownloadWorker, aResource: any, aHighPrSlotUsed: boolean) {
        this.downloadWorker = aDownloadWorker;
        this.resource = aResource;
        this.highPrSlotUsed = aHighPrSlotUsed;

        this.decodedBase64Data = null;
        if (this.resource.localB64Data !== "") {
            this.decodedBase64Data = TransformData.base64ToUint8Array(this.resource.localB64Data);
        }
    }

    public download() {
        ctx.postMessage({
            type: 'downloadResource',
            url: this.resource.url,
            uri: encodeURI(this.resource.url),
            highPrSlotUsed: this.highPrSlotUsed,
            decodedBase64Data: this.decodedBase64Data
        });
    }
}

export
class DownloadSlots {
    public static normalSlots: number = 8;
    public static highPrSlots: number = 4;

    public static freeHighPrSlots: number = DownloadSlots.highPrSlots;
    public static freeNormalSlots: number = DownloadSlots.normalSlots;
}

export default
class DownloadWorker {
    private resources: any = {};
    private orderedResourcesToDownload: any;
    private bboxes: any = {};

    constructor() {
        this.resources = {};
        this.bboxes = {};
        this.orderedResourcesToDownload = [];
    }

    public setDownloadSlotsSize(aMessage: { normalSlots: number, highPrSLots: number }) {
        DownloadSlots.normalSlots = aMessage.normalSlots;
        DownloadSlots.highPrSlots = aMessage.highPrSLots;
    }

    public addResource(aMessage: any) {
        let resource = this.resources[aMessage.url];
        if (resource === undefined) {
            resource = {
                url: aMessage.url,
                priority: aMessage.priority,
                localB64Data: aMessage.localB64Data,
                required: aMessage.required,
                bboxes: {}
            };

            this.resources[aMessage.url] = resource;
            this.orderedResourcesToDownload.push(resource);
        }
    }

    public removeResource(aUrl: string) {
        let resource = this.resources[aUrl];
        if (resource !== undefined) {
            delete this.resources[aUrl];
        }

        // this is external resources remove request from download manager
        for (let i = 0; i !== this.orderedResourcesToDownload.length; i++) {
            if (this.orderedResourcesToDownload[i].url === aUrl) {
                this.orderedResourcesToDownload.splice(i, 1);
                break;
            }
        }
    }

    public attachShapeBBoxes(aMessage: any) {
        let resource = this.resources[aMessage.url];
        if (resource !== undefined) {
            for (let i = 0; i < aMessage.bboxes.length; i++) {
                let bbox = aMessage.bboxes[i];
                resource.bboxes[bbox[0]] = new BoundingBox(bbox[1], bbox[2]);
            }
        }
    }

    public updateShapeBBox(aMessage: any) {
        let resource = this.resources[aMessage.url];
        if (resource !== undefined) {
            let bbox = resource.bboxes[aMessage.shapeUID];
            if (bbox !== undefined) {
                bbox.min = aMessage.bboxMin;
                bbox.max = aMessage.bboxMax;
            }
        }
    }

    public detachShapeBBox(aMessage: any) {
        let resource = this.resources[aMessage.url];
        if (resource !== undefined) {
            let bbox = resource.bboxes[aMessage.shapeUID];
            if (bbox !== undefined) {
                delete resource.bboxes[aMessage.shapeUID];
            }
        }
    }

    public recomputeDownloadPriority(aMessage: any) {
        let eye = aMessage.eyes[0];
        let mvp = aMessage.mvps[0];

        // frustum for basic visibility determination
        let viewFrustum = new FrustumVolume(mvp);

        let visible;
        let distance;
        let eyeBBoxVec = vec3.create();
        let diff;
        let resource;
        let bbox;
        for (let i = 0; i < this.orderedResourcesToDownload.length; i++) {

            resource = this.orderedResourcesToDownload[i];
            visible = false;
            distance = 9007199254740992; // max int number

            for (let key in resource.bboxes) {
                bbox = resource.bboxes[key];
                visible = visible || (viewFrustum.intersect(bbox, 0) !== -1);

                // DISTANCE between eye and center
                vec3.subtract(eyeBBoxVec, eye, bbox.center);
                diff = vec3.length(eyeBBoxVec);

                if (diff < (bbox.diameter / 2.0)) {
                    diff = 0;
                }

                // find the closest box to the eye which uses current texture
                if (diff < distance) {
                    distance = diff;
                }
            }
            resource.distance = distance;
            resource.visible = visible;
        }

        this.orderedResourcesToDownload.sort(function(queryA, queryB) {

            if (queryA.required && !queryB.required) {
                return -1;
            }
            if (queryB.required && !queryA.required) {
                return 1;
            }


            // we do not care about priority as the required flag has highest priority
            if (queryA.required && queryB.required) {
                if (queryA.visible && !queryB.visible) {
                    return -1;
                }
                if (queryB.visible && !queryA.visible) {
                    return 1;
                }

                // if both queries have zero priority and both are visible/or invisible, then smaller distance has higher weight
                if (queryA.distance < queryB.distance) {
                    return -1;
                }
                if (queryB.distance < queryA.distance) {
                    return 1;
                }

                return 0;
            }

            // if both queries priority are different from zero then the visibility has the highest weight
            if (queryA.visible && !queryB.visible) {
                return -1;
            }
            if (queryB.visible && !queryA.visible) {
                return 1;
            }

            // in case the visibility is the same then the priority has the highest wheigh
            if (queryA.priority < queryB.priority) {
                return -1;
            }
            if (queryB.priority < queryA.priority) {
                return 1;
            }

            // finaly te distance take its place in the priority weight determination
            if (queryA.distance < queryB.distance) {
                return -1;
            }
            if (queryB.distance < queryA.distance) {
                return 1;
            }

            return 0;
        });

        ctx.postMessage({type: 'downloadPrioritiesRecomputed'});
    }

    public returnSlots(aMessage: any) {
        DownloadSlots.freeHighPrSlots += aMessage.slots.highPr;
        DownloadSlots.freeNormalSlots += aMessage.slots.normal;
    }

    /**
     * Periodically check if we have some download requests to be served
     */
    public checkDownloadRequests() {
        // grab high priority resources for free slots at downloader in main thread
        let freeHighPrSlots = DownloadSlots.freeHighPrSlots;
        for (let i = 0; i < freeHighPrSlots; i++) {
            if (this.orderedResourcesToDownload.length > 0) {
                if (this.orderedResourcesToDownload[0].priority === 0) {
                    let resource = this.orderedResourcesToDownload[0];

                    // create download query
                    let query = new DownloadQuery(this, resource, true);
                    query.download();

                    // remove the resource as it will be downloaded in the main loop using DownloadQuery
                    this.removeResource(resource.url);

                    DownloadSlots.freeHighPrSlots--;
                }
            }
        }

        // grab resources for free slots at downloader in main thread
        let freeNormalSlots = DownloadSlots.freeNormalSlots;
        for (let i = 0; i < freeNormalSlots; i++) {
            if (this.orderedResourcesToDownload.length > 0) {
                let resource = this.orderedResourcesToDownload[0];

                // create download query and start downloading of the resource
                let query = new DownloadQuery(this, resource, false);
                query.download();

                // remove the resource as it will be downloaded in the main loop using DownloadQuery
                this.removeResource(resource.url);

                DownloadSlots.freeNormalSlots--;
            }
        }
    }

    public acceptMessage(e: MessageEvent) {
        let message = e.data;
        switch (message.type) {
            case 'resourceMessages':
                for (let i = 0; i != message.resourceMessages.length; i++) {
                    let resourceMessage = message.resourceMessages[i];

                    switch (resourceMessage.type) {
                        case 'addResource':
                            this.addResource(resourceMessage);
                            break;
                        case 'removeResource':
                            this.removeResource(resourceMessage.url);
                            break;
                        case 'attachShapeBBoxes':
                            this.attachShapeBBoxes(resourceMessage);
                            break;
                        case 'updateShapeBBox':
                            this.updateShapeBBox(resourceMessage);
                            break;
                        case 'detachShapeBBox':
                            this.detachShapeBBox(resourceMessage);
                            break;
                    }
                }
                this.checkDownloadRequests();
                break;
            case 'returnSlots':
                this.returnSlots(message);
                this.checkDownloadRequests();
                break;
            case 'recomputeDownloadPriority':
                this.recomputeDownloadPriority(message);
                break;
            case 'setDownloadSlotsSize':
                this.setDownloadSlotsSize(message);
                break;
        }
    }
}

let downloadWorker = new DownloadWorker();

ctx.addEventListener('message', function(e) {
    downloadWorker.acceptMessage(e);
}, false);
